// Three short, distinct end-of-call beeps when a call ends.
// Uses Web Audio API (with gesture unlock + sink routing) and HTMLAudioElement fallback;
// safely no-ops in environments without audio support (e.g. SSR/tests).

let sharedCtx: AudioContext | null = null;
let unlockBound = false;
let cachedWavUri: string | null = null;
let cachedRingWavUri: string | null = null;

function getAudioContextConstructor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null;
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ??
    null
  );
}

function ensureSharedContext(): AudioContext | null {
  const Ctx = getAudioContextConstructor();
  if (!Ctx) return null;
  if (!sharedCtx || sharedCtx.state === 'closed') {
    sharedCtx = new Ctx();
  }
  return sharedCtx;
}

export function warmCallEndTone(): void {
  try {
    const ctx = ensureSharedContext();
    if (ctx && ctx.state === 'suspended') {
      void ctx.resume().catch(() => {});
    }
    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default' &&
      typeof Notification.requestPermission === 'function'
    ) {
      void Notification.requestPermission().catch(() => {});
    }
  } catch {
    // Ignore audio context init errors.
  }
}

if (typeof window !== 'undefined' && !unlockBound) {
  unlockBound = true;
  const unlock = () => warmCallEndTone();
  window.addEventListener('pointerdown', unlock, { passive: true });
  window.addEventListener('keydown', unlock, { passive: true });
}

function buildBeepWavDataUri(): string {
  if (cachedWavUri) return cachedWavUri;
  const sampleRate = 8000;
  const beepDuration = 0.11;
  const beepGap = 0.07;
  const totalSec = 3 * (beepDuration + beepGap);
  const numSamples = Math.ceil(sampleRate * totalSec);
  const pcm = new Int16Array(numSamples);

  for (let b = 0; b < 3; b++) {
    const startSec = b * (beepDuration + beepGap);
    const startSample = Math.floor(startSec * sampleRate);
    const lenSamples = Math.floor(beepDuration * sampleRate);
    for (let i = 0; i < lenSamples && startSample + i < numSamples; i++) {
      const t = i / sampleRate;
      const attack = Math.min(1, t / 0.012);
      const release = Math.min(1, (beepDuration - t) / 0.018);
      const env = Math.max(0, Math.min(attack, release));
      const sample = Math.sin(2 * Math.PI * 480 * t) * env * 0.22;
      pcm[startSample + i] = Math.round(sample * 32767);
    }
  }

  const dataBytes = pcm.length * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, dataBytes, true);
  const out = new Int16Array(buffer, 44);
  out.set(pcm);

  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  cachedWavUri = `data:audio/wav;base64,${btoa(binary)}`;
  return cachedWavUri;
}

function scheduleOscillatorBeeps(ctx: AudioContext): void {
  const beepDuration = 0.11;
  const beepGap = 0.07;
  const baseTime = ctx.currentTime + 0.015;
  for (let i = 0; i < 3; i++) {
    const start = baseTime + i * (beepDuration + beepGap);
    const end = start + beepDuration;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(480, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.18, start + 0.012);
    gain.gain.setValueAtTime(0.18, end - 0.018);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(end + 0.005);
  }
}

export function playCallEndBeeps(sinkId?: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    const ctx = ensureSharedContext();
    if (ctx) {
      const sinkCapable = ctx as AudioContext & { setSinkId?: (id: string) => Promise<void> };
      if (sinkId && typeof sinkCapable.setSinkId === 'function') {
        void sinkCapable.setSinkId(sinkId).catch(() => {});
      }
      if (ctx.state === 'suspended') {
        void ctx
          .resume()
          .then(() => scheduleOscillatorBeeps(ctx))
          .catch(() => {});
      } else {
        scheduleOscillatorBeeps(ctx);
      }
      // If a specific output device is selected and AudioContext lacks setSinkId, also route via HTMLAudioElement.
      if (!sinkId || typeof sinkCapable.setSinkId === 'function') {
        return;
      }
    }
    if (typeof Audio !== 'undefined') {
      const audio = new Audio(buildBeepWavDataUri());
      const sinkAudio = audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
      if (sinkId && typeof sinkAudio.setSinkId === 'function') {
        void sinkAudio
          .setSinkId(sinkId)
          .then(() => audio.play())
          .catch(() => {
            void audio.play().catch(() => {});
          });
      } else {
        void audio.play().catch(() => {});
      }
    }
  } catch {
    // Audio playback may fail if browser autoplay policy blocks it; ignore safely.
  }
}

function buildRingtoneWavDataUri(): string {
  if (cachedRingWavUri) return cachedRingWavUri;
  const sampleRate = 8000;
  const totalSec = 3.0;
  const numSamples = Math.ceil(sampleRate * totalSec);
  const pcm = new Int16Array(numSamples);
  const pulses: readonly [number, number][] = [
    [0.0, 0.4],
    [0.6, 1.0],
  ];

  for (const [startSec, endSec] of pulses) {
    const startSample = Math.floor(startSec * sampleRate);
    const lenSamples = Math.floor((endSec - startSec) * sampleRate);
    const dur = endSec - startSec;
    for (let i = 0; i < lenSamples && startSample + i < numSamples; i++) {
      const t = i / sampleRate;
      const attack = Math.min(1, t / 0.02);
      const release = Math.min(1, (dur - t) / 0.03);
      const env = Math.max(0, Math.min(attack, release));
      const sample =
        (Math.sin(2 * Math.PI * 440 * t) + Math.sin(2 * Math.PI * 480 * t)) * 0.5 * env * 0.34;
      pcm[startSample + i] = Math.round(sample * 32767);
    }
  }

  const dataBytes = pcm.length * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, dataBytes, true);
  const out = new Int16Array(buffer, 44);
  out.set(pcm);

  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  cachedRingWavUri = `data:audio/wav;base64,${btoa(binary)}`;
  return cachedRingWavUri;
}

function scheduleRingPulse(ctx: AudioContext): void {
  const pulses: readonly [number, number][] = [
    [0.02, 0.42],
    [0.62, 1.02],
  ];
  const base = ctx.currentTime;
  for (const [s, e] of pulses) {
    const start = base + s;
    const end = base + e;
    for (const freq of [440, 480]) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.14, start + 0.02);
      gain.gain.setValueAtTime(0.14, end - 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(end + 0.005);
    }
  }
}

export function startIncomingRingtone(sinkId?: string | null): () => void {
  if (typeof window === 'undefined') return () => {};
  let stopped = false;
  let audio: HTMLAudioElement | null = null;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  const startWebAudioFallback = () => {
    if (stopped || intervalId !== null) return;
    try {
      const ctx = ensureSharedContext();
      if (!ctx) return;
      const sinkCapable = ctx as AudioContext & { setSinkId?: (id: string) => Promise<void> };
      if (sinkId && typeof sinkCapable.setSinkId === 'function') {
        void sinkCapable.setSinkId(sinkId).catch(() => {});
      }
      const pulse = () => {
        if (stopped) return;
        if (ctx.state === 'suspended') {
          void ctx.resume().then(() => { if (!stopped) scheduleRingPulse(ctx); }).catch(() => {});
        } else {
          scheduleRingPulse(ctx);
        }
      };
      pulse();
      intervalId = setInterval(pulse, 3000);
    } catch {
      // Ignore Web Audio errors.
    }
  };
  try {
    if (typeof Audio !== 'undefined') {
      audio = new Audio(buildRingtoneWavDataUri());
      audio.loop = true;
      const sinkAudio = audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
      const startPlay = () => {
        if (stopped || !audio) return;
        void audio.play().catch(() => {
          startWebAudioFallback();
        });
      };
      if (sinkId && typeof sinkAudio.setSinkId === 'function') {
        void sinkAudio.setSinkId(sinkId).then(startPlay).catch(startPlay);
      } else {
        startPlay();
      }
    } else {
      startWebAudioFallback();
    }
  } catch {
    startWebAudioFallback();
  }
  return () => {
    stopped = true;
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (audio) {
      try {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      } catch {
        // Ignore cleanup errors.
      }
      audio = null;
    }
  };
}

